import { generatePath, Link as RouterLink } from 'react-router-dom';
import { AccountPrivilege } from 'src/core/hooks/account';

export interface AppRouteComponentProps {
    component: any; // Todo fix type
    to?: string;
    href?: string;
}

export interface AppRouteConfig {
    accessPrivilege?: AccountPrivilege | AccountPrivilege[];
    thumbnail?: string;
    image?: string;
    infoBadgeCount?: number;
    slug?: string;
}

export class AppRoute {
    path: string;
    t_key: string;
    t_params: Record<string, string>;
    config: AppRouteConfig;

    constructor(path: string, t_key: string, config?: AppRouteConfig) {
        this.path = path;
        this.t_key = t_key;
        this.t_params = {};
        this.config = config ?? {};
    }

    sub(subPath: string, t_key: string, config?: AppRouteConfig) {
        const route = new AppRoute(`${this.path}/${subPath}`, t_key, {
            accessPrivilege: this.config.accessPrivilege,
            ...config,
        });
        return route;
    }

    tt(key: string) {
        return `routes:${this.t_key}.${key}`;
    }

    get t_name(): string {
        return this.tt('name');
    }

    get t_short(): string {
        return this.tt('short');
    }

    get t_long(): string {
        return this.tt('long');
    }

    /* Config Getter / Setter */
    get image(): string | undefined {
        return this.config.image;
    }

    set image(image: string | undefined) {
        this.config.image = image;
    }

    get thumbnail(): string | undefined {
        return this.config.thumbnail;
    }

    set thumbnail(thumbnail: string | undefined) {
        this.config.thumbnail = thumbnail;
    }

    get accessPrivilege(): AccountPrivilege | AccountPrivilege[] | undefined {
        return this.config.accessPrivilege;
    }

    get infoBadgeCount(): number | undefined {
        return this.config.infoBadgeCount;
    }

    set infoBadgeCount(infoBadgeCount: number | undefined) {
        this.config.infoBadgeCount = infoBadgeCount;
    }

    get componentProps(): AppRouteComponentProps {
        return {
            component: RouterLink,
            to: this.path,
        };
    }

    get slug(): string {
        return this.config.slug ?? this.path.replace(/\/$/, '').split('/').pop()!;
    }

    set slug(slug: string | undefined) {
        this.config.slug = slug;
    }

    interpolatedPath(params?: Record<string, string>): string {
        return generatePath(this.path, params);
    }

    interpolatedClone(params?: Record<string, string>, query?: Record<string, string>): AppRoute {
        const clone = new AppRoute(this.interpolatedPath(params), this.t_key, { ...this.config });
        const queryStr = new URLSearchParams(query).toString();

        if (queryStr !== '') clone.path += `?${queryStr}`;

        return clone;
    }

    clone(config: AppRouteConfig = {}) {
        const clone = new AppRoute(this.path, this.t_key, { ...this.config, ...config });
        return clone;
    }

    equals(other: AppRoute) {
        return this.path === other.path;
    }
}
